/**
 * Terminal display transform: scan, plan, apply — nothing else in this layer
 * mutates a tree.
 *
 * Runs after `rehypeInferTableTitle`, so every source-model consumer has already
 * read an undecorated tree. Registers only under `polly-editorial-v1`; the
 * streaming-tail anchor is a separate, policy-independent pass.
 */
import {
    classifyEditorialCapabilities,
    isEditorialTouchable,
    type EditorialHastNode,
} from "./editorial-capabilities";
import {
    POLLY_EDITORIAL_V1,
    type EditorialPolicyManifest,
    type EditorialSemantic,
} from "./editorial-policy";
import {
    applyEditPlan,
    validateEditPlan,
    type EditorialSplice,
} from "./editorial-edit-plan";
import { buildInlineRun, isInlineWrapperTag } from "./editorial-inline-run";
import { markEditorial } from "./editorial-provenance";
import {
    resolveRemovalSeam,
    scanEditorialText,
    type EditorialTextSegment,
} from "./emoji-typesetting";

interface HastNodeLike {
    type?: string;
    tagName?: string;
    value?: string;
    properties?: Record<string, unknown>;
    children?: HastNodeLike[];
}

export interface EditorialTypesettingOptions {
    readonly policy?: EditorialPolicyManifest;
}

const glyphNode = (semantic: EditorialSemantic): HastNodeLike =>
    markEditorial(
        {
            type: "element",
            tagName: "span",
            // The visible glyph and its accessible name are the renderer's job;
            // this pass emits the marker and the semantic it resolved. The raw
            // emoji is deliberately NOT kept as text — leaving it would show the
            // vendor glyph the layer exists to replace.
            properties: { dataEditorialGlyph: semantic },
            children: [],
        } as HastNodeLike,
        { kind: "glyph", semantic }
    );

const textNode = (value: string): HastNodeLike => ({ type: "text", value });

/**
 * One run's worth of splices, in run-text coordinates translated to leaves.
 *
 * A token can straddle leaves when a sequence is split across text nodes, so the
 * first range carries the replacement and the rest are deletions — never two
 * insertions of the same glyph.
 */
interface PlannedEdit {
    readonly start: number;
    readonly end: number;
    readonly nodes: readonly HastNodeLike[];
}

/**
 * Token edits in run-text coordinates, with adjacent decorative removals merged.
 *
 * Merging is not an optimisation. Each glyph's seam claims the whitespace on
 * both sides of itself, so two decoratives separated by a single space both
 * claim that space and their spans overlap — `validateEditPlan` then rejects the
 * whole plan and the run silently keeps both glyphs. Merging first means the
 * seam is resolved ONCE, for the whole removed region, against its true
 * neighbours.
 *
 * The merge test is "do their seams contend", asked of `resolveRemovalSeam`
 * itself rather than by re-deriving what counts as adjacent — the two would
 * drift apart.
 */
const plannedEdits = (
    text: string,
    segments: readonly EditorialTextSegment[]
): PlannedEdit[] => {
    const edits: PlannedEdit[] = [];
    let pending: { start: number; end: number } | null = null;

    const flushDecorative = (): void => {
        if (pending === null) return;

        const seam = resolveRemovalSeam(text, pending.start, pending.end);
        edits.push({
            start: seam.start,
            end: seam.end,
            nodes: seam.replacement === "" ? [] : [textNode(seam.replacement)],
        });
        pending = null;
    };

    for (const segment of segments) {
        if (segment.kind !== "emoji") continue;

        if (segment.disposition === "DECORATIVE") {
            if (pending !== null) {
                const open = resolveRemovalSeam(
                    text,
                    pending.start,
                    pending.end
                );
                const next = resolveRemovalSeam(
                    text,
                    segment.start,
                    segment.end
                );

                if (open.end > next.start) {
                    pending = { start: pending.start, end: segment.end };
                    continue;
                }
                flushDecorative();
            }

            pending = { start: segment.start, end: segment.end };
            continue;
        }

        // A non-decorative token between two decoratives keeps them apart, so
        // the pending group has to close before this one is placed.
        flushDecorative();

        if (
            segment.disposition === "SEMANTIC" &&
            segment.semantic !== undefined
        ) {
            edits.push({
                start: segment.start,
                end: segment.end,
                nodes: [glyphNode(segment.semantic)],
            });
        }
        // PRESERVE contributes no edit at all. There is no generic-strip path.
    }

    flushDecorative();
    return edits;
};

/**
 * One run's worth of splices, translated from run-text coordinates to leaves.
 *
 * A token can straddle leaves when a sequence is split across text nodes, so the
 * first range carries the replacement and the rest are deletions — never two
 * insertions of the same glyph.
 */
const splicesForRun = (
    run: ReturnType<typeof buildInlineRun>,
    policy: EditorialPolicyManifest
): EditorialSplice[] => {
    const scanned = scanEditorialText(run.text, { policy });
    // Emoji-free runs allocate nothing beyond the scan's own fast path.
    if (!scanned.changed) return [];

    const splices: EditorialSplice[] = [];

    for (const edit of plannedEdits(run.text, scanned.segments)) {
        const ranges = run.sliceToLeafRanges(edit.start, edit.end);
        if (ranges.length === 0) continue;

        ranges.forEach((range, index) => {
            if (index === 0 && edit.nodes.length > 0) {
                splices.push({
                    kind: "replace",
                    leaf: range.leaf,
                    start: range.start,
                    end: range.end,
                    nodes: edit.nodes,
                });
                return;
            }
            splices.push({
                kind: "delete",
                leaf: range.leaf,
                start: range.start,
                end: range.end,
            });
        });
    }

    return splices;
};

/**
 * Transform one block's inline run. Returns without touching the tree when the
 * plan does not validate — which is how an interactive container whose whole
 * label would be consumed rolls back rather than losing its affordance.
 */
const typesetRun = (
    block: HastNodeLike,
    policy: EditorialPolicyManifest
): void => {
    const run = buildInlineRun(block as EditorialHastNode);
    if (run.text === "") return;

    const splices = splicesForRun(run, policy);
    if (splices.length === 0) return;

    const plan = { splices };
    const report = validateEditPlan(plan, { block });
    if (!report.valid) return;
    applyEditPlan(plan, { block }, report);

    // Record that THIS transform emptied the block, while the evidence still
    // exists. After apply, "empty" and "emptied" are indistinguishable by
    // looking — an authored-empty container and one this layer just cleared
    // are the same tree — and only the plan knew the difference.
    if (report.containerOutcome === "suppress") {
        markEditorial(block, { kind: "emptied-container" });
    }
};

/**
 * Whether this element owns inline content directly, rather than delegating it
 * to a block child. A loose list item delegates to its paragraph and a tight one
 * does not; asking the question this way means both are transformed exactly
 * once, at whichever element actually holds the text.
 */
const ownsInlineContent = (node: HastNodeLike): boolean =>
    (node.children ?? []).some(
        (child) =>
            (child.type === "text" && (child.value ?? "") !== "") ||
            (child.type === "element" &&
                isInlineWrapperTag(child.tagName ?? ""))
    );

const visit = (node: HastNodeLike, policy: EditorialPolicyManifest): void => {
    const stack = [...(node.children ?? [])].reverse();
    const seen = new Set<unknown>([node]);

    while (stack.length > 0) {
        const child = stack.pop() as HastNodeLike;
        if (seen.has(child)) continue;
        seen.add(child);
        if (child.type !== "element") continue;

        const { contract } = classifyEditorialCapabilities(
            child as EditorialHastNode
        );
        if (!isEditorialTouchable(contract)) continue;

        if (
            !isInlineWrapperTag(child.tagName ?? "") &&
            ownsInlineContent(child)
        ) {
            typesetRun(child, policy);
        }

        const children = child.children ?? [];
        for (let index = children.length - 1; index >= 0; index--) {
            stack.push(children[index]);
        }
    }
};

/**
 * Configured factory: returns the rehype PLUGIN, so registration reads
 * `.use(rehypeApplyEditorialTypesetting({ policy }))`. The extra level is what
 * `unified().use()` expects — handing it a bare transformer makes it call the
 * transformer at freeze time, with no tree.
 */
export const rehypeApplyEditorialTypesetting =
    (options: EditorialTypesettingOptions = {}) =>
    () =>
    (tree: unknown): void => {
        visit(tree as HastNodeLike, options.policy ?? POLLY_EDITORIAL_V1);
    };
